import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';

/**
 * Editable combobox for the hero's Condition field.
 *
 * Replaces a native `<datalist>`, which was the wrong primitive here for three
 * reasons: `autocomplete="off"` silently suppresses its popup in Chrome, it
 * generally will not open on click with an empty input (it waits for typing),
 * and being browser chrome it cannot be styled to match the surrounding glass.
 *
 * Still free text — people describe how they feel in their own words, so an
 * unmatched entry is submitted as typed rather than rejected.
 *
 * Follows the ARIA combobox pattern: the input owns `role="combobox"` with
 * `aria-expanded` / `aria-controls`, and highlights the active option via
 * `aria-activedescendant` so focus never actually leaves the input.
 */
export default function ConditionCombobox({
  value,
  onChange,
  options = [],
  label = 'Condition',
  // Mirrors the "Any time" / "Myself" defaults on the sibling selects, so the
  // three fields read as a set rather than one empty slot next to two filled
  // ones. Styled at full value weight rather than as faint ghost text, and it
  // still submits an empty condition — "any" is genuinely the default here.
  placeholder = 'Any condition',
  inputId = 'mm-condition',
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const listId = `${useId()}-listbox`;

  // Typing filters; an empty field shows everything, which is the whole point
  // of opening on click.
  //
  // A value that exactly matches an option also shows everything. Without that
  // exception, choosing "Depression" left the field filtering on its own
  // result, so reopening offered a list of one and you could not switch to a
  // different condition without first deleting the text. Native selects always
  // show the full list with the current item ticked; this matches them.
  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return options;
    if (options.some((o) => o.name.toLowerCase() === q)) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [value, options]);

  // Close when focus or a click goes elsewhere. Pointerdown rather than click
  // so the list is gone before a click on something behind it lands.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onFocusIn = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [open]);

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const choose = (option) => {
    onChange(option.name);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    // ArrowDown opens the list when closed, rather than doing nothing.
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(0);
      return;
    }
    if (!open) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % Math.max(matches.length, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => (i <= 0 ? matches.length - 1 : i - 1));
        break;
      case 'Enter':
        // Only intercept Enter when a suggestion is highlighted; otherwise let
        // it submit the search with whatever was typed.
        if (activeIndex >= 0 && matches[activeIndex]) {
          event.preventDefault();
          choose(matches[activeIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="mm-combo" ref={wrapRef}>
      <label className="mm-search__label" htmlFor={inputId}>
        {label}
      </label>

      <input
        id={inputId}
        className="mm-search__control"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && activeIndex >= 0 ? `${listId}-opt-${activeIndex}` : undefined
        }
        // Off so the browser's own saved-value dropdown doesn't fight ours.
        // Unlike with a datalist, that costs us nothing here.
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        // Opening on click is the behaviour a datalist could not give us.
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={handleKeyDown}
      />

      {open && (
        <ul className="mm-combo__list" id={listId} role="listbox" ref={listRef}>
          {matches.length === 0 && (
            <li className="mm-combo__empty">
              <FiSearch size={13} aria-hidden="true" />
              No match &mdash; search for &ldquo;{value.trim()}&rdquo; anyway
            </li>
          )}

          {matches.map((option, index) => {
            // Native select menus tick the current value; mirroring that keeps
            // this consistent with the two popups beside it.
            const selected =
              value.trim().toLowerCase() === option.name.toLowerCase();

            return (
              <li key={option.id ?? option.name}>
                <button
                  type="button"
                  id={`${listId}-opt-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={selected}
                  className={`mm-combo__option ${
                    index === activeIndex ? 'mm-combo__option--active' : ''
                  } ${selected ? 'mm-combo__option--selected' : ''}`}
                  // Mousedown would blur the input before click fires.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(option)}
                >
                  <span className="mm-combo__check" aria-hidden="true">
                    {selected ? '✓' : ''}
                  </span>
                  {option.name}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
